import Icon from "@material-ui/core/Icon"
import React, { useState } from "react"

import { ActionType } from "../../common/transaction"
import {
  ArrDepSorting,
  FlightTableTab,
  IFlightTableSelection,
  updateChosenArrDepSortingSelection,
  updateChosenFlighTableTabSelection
} from "../../containers/selectionSlice"

import { FragmentBtn } from "../BreakoutPeriod"
import Button from "../generic/buttons/Button"
import DropdownButton, { DropdownLink } from "../generic/buttons/DropdownButton"
import { IMainTable } from "./FlightTableGrid"
import { ReactComponent as TabCondensedIcon } from "../../img/icons/table_view_condensed_icon.svg"
import DownloadPane from "../DownloadPane"
import { PageId } from "../../common/pageId"
import { BtnText } from "../../common/text"
import { useAppDispatch, useAppSelector } from "../../app/hooks"

interface ILinkType {
  [actionType: string]: DropdownLink
}

interface Props extends IMainTable {
  onExport: (exportFormat: string) => void
  isViewPageType?: boolean
  isHandlingPages?: boolean
  disabledControls: string[]
}

export const FlightTableTopBar: React.FunctionComponent<Props> = ({
  onChangeBreakout,
  breakoutValues,
  onActionClick,
  fragmentConfig,
  actionConfig,
  onRefresh,
  airportSeasons,
  pageId,
  onExport,
  onDownload,
  disableActionBtns,
  historyRowAirports,
  isViewPageType,
  isHandlingPages,
  disabledControls
}) => {
  const dispatch = useAppDispatch()

  const { chosenFlightTableTab, chosenArrDepSorting }: IFlightTableSelection =
    useAppSelector((state) => state["selectionReducer"].flightTableSelection)
  const isEditOffersPage = pageId === PageId.EditOffers
  const [showFragment, setShowFragment] = useState(false)

  const handleToggleShowFragmentFields = () => {
    if (showFragment) {
      onChangeBreakout({
        separationOption: "",
        doop: "",
        splitOption: "",
        startDate: null,
        endDate: null
      })
    }
    setShowFragment(!showFragment)
  }

  const handleActionClick = (actionType: ActionType) => {
    onActionClick(actionType)
    setShowFragment(false)
  }

  const handleChangeTab = (tab: FlightTableTab) => {
    dispatch(updateChosenFlighTableTabSelection(tab))
  }

  const handleChangeArrDepSorting = (sortBy: ArrDepSorting) => {
    dispatch(updateChosenArrDepSortingSelection(sortBy))
  }

  const handleGetDropdownLinks = (linkTypes: ILinkType): DropdownLink[] => {
    const links = []

    Object.keys(linkTypes).forEach((actionKey) => {
      const action = ActionType[actionKey]
      if (!actionConfig?.includes(action)) {
        links.push(linkTypes[actionKey])
      }
    })

    return links
  }

  const changeLinks: DropdownLink[] = handleGetDropdownLinks({
    [ActionType.CHANGE]: {
      label: "Change",
      onClick: () => handleActionClick(ActionType.CHANGE),
      title: "Change flight (C/R)"
    },
    [ActionType.SHLCHANGENOOFFER]: {
      label: "Change No Offer",
      onClick: () => handleActionClick(ActionType.SHLCHANGENOOFFER),
      title: "Change no offer (C/L)"
    },
    [ActionType.SHLCHANGECONTINUATION]: {
      label: "Change Continuation",
      onClick: () => handleActionClick(ActionType.SHLCHANGECONTINUATION),
      title: "Change continuation (C/I)"
    }
  })

  const swapLinks: DropdownLink[] = handleGetDropdownLinks({
    [ActionType.SWAP]: {
      label: "Swap Time",
      onClick: () => handleActionClick(ActionType.SWAP),
      title: "Swap times of two flights (C/R)"
    },
    [ActionType.SWAPLINK]: {
      label: "Swap Link",
      onClick: () => handleActionClick(ActionType.SWAPLINK),
      title: "Swap departure of linked flights (C/R)"
    },
    [ActionType.SWAPAC]: {
      label: "Swap A/C",
      onClick: () => handleActionClick(ActionType.SWAPAC),
      title: "Swap A/C type and seats (C/R)"
    },
    [ActionType.SWAPPERIOD]: {
      label: "Swap Period",
      onClick: () => handleActionClick(ActionType.SWAPPERIOD),
      title: "Swap times and periods of two flights (C/R)"
    },
    [ActionType.SWAPARRTODEP]: {
      label: "Swap Arr to Dep",
      onClick: () => handleActionClick(ActionType.SWAPARRTODEP),
      title: "Change arrival flight to departure"
    },
    [ActionType.SWAPDEPTOARR]: {
      label: "Swap Dep to Arr",
      onClick: () => handleActionClick(ActionType.SWAPDEPTOARR),
      title: "Change departure flight to arrival"
    }
  })

  const showAction = (type: ActionType): boolean => {
    return !actionConfig?.includes(type)
  }

  const disabledAction = (type: ActionType): boolean => {
    const toLowerCase = disabledControls.map((element) => {
      return element.toLowerCase()
    })

    return toLowerCase.includes(type.toLowerCase())
  }

  return (
    <div className="ocs-flight-table-top-bar d-flex flex-column">
      <section
        className={
          "ocs-flight-table-top-bar-btn " +
          (!onActionClick ? "justify-content-end" : "")
        }
      >
        {onActionClick && (
          <div className="d-flex ocs-action-btns">
            {!isViewPageType && (
              <div
                className={`d-flex ${
                  !isHandlingPages ? "ocs-action-btns-right" : ""
                }`}
              >
                {!isEditOffersPage && !isHandlingPages && (
                  <div className="d-flex">
                    <FragmentBtn
                      values={breakoutValues}
                      onChange={onChangeBreakout}
                      fragmentConfig={fragmentConfig}
                      showFragment={showFragment}
                      onToggleShowFragment={handleToggleShowFragmentFields}
                      disabled={disableActionBtns}
                    />
                  </div>
                )}
                <div
                  className={`d-flex ocs-action-btns-wrapper  ${
                    isEditOffersPage ? "no-border" : ""
                  }`}
                >
                  {showAction(ActionType.ACCEPT) && (
                    <Button
                      className="ocs-btn-link"
                      onClick={() => handleActionClick(ActionType.ACCEPT)}
                      title="Accept offer (A)"
                      disabled={
                        disableActionBtns || disabledAction(ActionType.ACCEPT)
                      }
                    >
                      {BtnText.accept}
                    </Button>
                  )}
                  {showAction(ActionType.PEND) && (
                    <Button
                      className="ocs-btn-link"
                      onClick={() => handleActionClick(ActionType.PEND)}
                      title="Pend offer (P)"
                      disabled={
                        disableActionBtns || disabledAction(ActionType.PEND)
                      }
                    >
                      {BtnText.pend}
                    </Button>
                  )}
                  {showAction(ActionType.DECLINE) && (
                    <Button
                      className="ocs-btn-link"
                      onClick={() => handleActionClick(ActionType.DECLINE)}
                      title="Decline offer (Z)"
                      disabled={
                        disableActionBtns || disabledAction(ActionType.DECLINE)
                      }
                    >
                      {BtnText.decline}
                    </Button>
                  )}
                </div>
                {(changeLinks.length > 0 || showAction(ActionType.DELETE)) && (
                  <div className="d-flex ocs-action-btns-wrapper">
                    {changeLinks.length === 1 ? (
                      <Button
                        className="ocs-btn-link"
                        onClick={() => handleActionClick(ActionType.CHANGE)}
                        title="Change flight (C/R)"
                        disabled={
                          disableActionBtns || disabledAction(ActionType.CHANGE)
                        }
                      >
                        {BtnText.change}
                      </Button>
                    ) : (
                      <DropdownButton
                        links={changeLinks}
                        disabled={
                          disableActionBtns || disabledAction(ActionType.CHANGE)
                        }
                      >
                        {BtnText.change}
                      </DropdownButton>
                    )}
                    {showAction(ActionType.DELETE) && (
                      <Button
                        className="ocs-btn-link"
                        onClick={() => handleActionClick(ActionType.DELETE)}
                        title="Delete flight (D)"
                        disabled={
                          disableActionBtns || disabledAction(ActionType.DELETE)
                        }
                      >
                        {BtnText.delete}
                      </Button>
                    )}
                  </div>
                )}
                {(showAction(ActionType.SEARCH) ||
                  showAction(ActionType.CLEARREQ)) && (
                  <div className="d-flex ocs-action-btns-wrapper">
                    {showAction(ActionType.SEARCH) && (
                      <Button
                        className="ocs-btn-link"
                        onClick={() => handleActionClick(ActionType.SEARCH)}
                        title="Request required time (C/R)"
                        disabled={
                          disableActionBtns || disabledAction(ActionType.SEARCH)
                        }
                      >
                        {BtnText.search}
                      </Button>
                    )}
                    {showAction(ActionType.CLEARREQ) && (
                      <Button
                        className="ocs-btn-link"
                        onClick={() => handleActionClick(ActionType.CLEARREQ)}
                        title="Clear required times (C/R)"
                        disabled={
                          disableActionBtns ||
                          disabledAction(ActionType.CLEARREQ)
                        }
                      >
                        {BtnText.clearReg}
                      </Button>
                    )}
                  </div>
                )}
                {(showAction(ActionType.MERGE) ||
                  showAction(ActionType.DELINK) ||
                  showAction(ActionType.LINK)) && (
                  <div
                    className={`d-flex ocs-action-btns-wrapper ${
                      isEditOffersPage && isHandlingPages ? "no-border" : ""
                    }`}
                  >
                    {showAction(ActionType.MERGE) && (
                      <Button
                        className="ocs-btn-link"
                        onClick={() => handleActionClick(ActionType.MERGE)}
                        title="Merge period fragments of same flight (CC../R)"
                        disabled={
                          disableActionBtns || disabledAction(ActionType.MERGE)
                        }
                      >
                        {BtnText.merge}
                      </Button>
                    )}

                    {showAction(ActionType.DELINK) && (
                      <Button
                        className="ocs-btn-link"
                        onClick={() => handleActionClick(ActionType.DELINK)}
                        title="Split turnaround flight in arrival and departure (CC/R)"
                        disabled={
                          disableActionBtns || disabledAction(ActionType.DELINK)
                        }
                      >
                        {BtnText.delink}
                      </Button>
                    )}
                    {showAction(ActionType.LINK) && (
                      <Button
                        className="ocs-btn-link"
                        onClick={() => handleActionClick(ActionType.LINK)}
                        title="Link arrival and departure flight (C/RR)"
                        disabled={
                          disableActionBtns || disabledAction(ActionType.LINK)
                        }
                      >
                        {BtnText.link}
                      </Button>
                    )}
                  </div>
                )}
                {swapLinks.length > 0 && (
                  <span
                    className={
                      showAction(ActionType.SHLHISTORICS)
                        ? "ocs-action-btns-wrapper"
                        : ""
                    }
                  >
                    <DropdownButton
                      links={swapLinks}
                      disabled={
                        disableActionBtns || disabledAction(ActionType.SWAP)
                      }
                    >
                      {BtnText.swap}
                    </DropdownButton>
                  </span>
                )}
                {showAction(ActionType.SHLHISTORICS) && (
                  <Button
                    className="ocs-btn-link"
                    onClick={() => handleActionClick(ActionType.SHLHISTORICS)}
                    title="Claim Historics (F)"
                    disabled={
                      disableActionBtns ||
                      disabledAction(ActionType.SHLHISTORICS)
                    }
                  >
                    {BtnText.claimHistorics}
                  </Button>
                )}

                {/* <DropdownButton
                links={[
                  { label: "Departure", onClick: () => {} },
                  { label: "Arrival", onClick: () => {} },
                  { label: "Turnaround", onClick: () => {} }
                ]}
              >
                New Flight
              </DropdownButton> */}
              </div>
            )}
          </div>
        )}
        <div className="d-flex ocs-flight-table-top-bar-btn">
          <DownloadPane
            pageId={pageId}
            airportSeasons={airportSeasons}
            onDownload={onDownload}
            onExport={onExport}
            historyRowAirports={historyRowAirports}
          />
          <Button
            className="ocs-btn-link btn-margin"
            onClick={onRefresh}
            title="Refresh Search"
            icon="refresh"
          />
          <div className="ocs-btn-sort-arr-dep">
            Sort Arr/Dep By:
            <DropdownButton
              links={[
                {
                  label: "Arr",
                  onClick: () => handleChangeArrDepSorting(ArrDepSorting.Arr)
                },
                {
                  label: "Dep",
                  onClick: () => handleChangeArrDepSorting(ArrDepSorting.Dep)
                },
                {
                  label: "Arr+Dep",
                  onClick: () => handleChangeArrDepSorting(ArrDepSorting.ArrDep)
                }
              ]}
              className="btn-margin"
            >
              {chosenArrDepSorting}
            </DropdownButton>
          </div>
          <div className="ocs-flight-table-tabs mq-flex-direction-row  d-flex">
            <div
              className={
                "ocs-flight-table-tab ocs-mr-6" +
                (chosenFlightTableTab === FlightTableTab.condensed
                  ? " ocs-flight-table-tab-active"
                  : "")
              }
              onClick={() => {
                handleChangeTab(FlightTableTab.condensed)
              }}
            >
              <TabCondensedIcon className="ocs-flight-table-tab-icon" />
            </div>
            <div
              className={
                "ocs-flight-table-tab ocs-mr-4" +
                (chosenFlightTableTab === FlightTableTab.default
                  ? " ocs-flight-table-tab-active"
                  : "")
              }
              onClick={() => {
                handleChangeTab(FlightTableTab.default)
              }}
            >
              <Icon className="ocs-md-btn-icon ocs-flight-table-tab-icon">
                grid_on
              </Icon>
            </div>
            <div
              className={
                "ocs-flight-table-tab " +
                (chosenFlightTableTab === FlightTableTab.spacious
                  ? " ocs-flight-table-tab-active"
                  : "")
              }
              onClick={() => {
                handleChangeTab(FlightTableTab.spacious)
              }}
            >
              <Icon className="ocs-md-btn-icon ocs-flight-table-tab-icon">
                window
              </Icon>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
